import type { DesignerNodeData, GetDesignerNodeDataById } from "@skilitics/nautilus-core-designer-data";

import type { DesignerNodeHandle, GetManifestByTypeId, NodeDesignerRole } from "../types/index.ts";
import type { GetMonolingualDesignerNodeData, MonolingualizeConfig } from "./contextualSelectors/index.ts";
import { getNodeProfileByIdProfiler } from "./getNodeProfileByIdProfiling.ts";

export type GetNodeProfileById = (id: number) => unknown;

/**
 * A clean identity handle from any node view, for embedding in profile values.
 * Returns a fresh object holding only handle fields so a profile value never
 * retains the node's `config`/`childNodes` subtree.
 *
 * Note the handle's `title` is whatever the view carries — inside `getProfile`
 * that is the raw node data (no computed title); consumers wanting the
 * computed title resolve it by id (`useGetNodeTitleAndIsPlaceholder`).
 */
export const toNodeHandle = (node: DesignerNodeHandle): DesignerNodeHandle => {
  const { id, typeId, role, isPlaceholder, title, version, owner, creator, upstreamTemplateId, triggers } = node;
  return { id, typeId, role, isPlaceholder, title, version, owner, creator, upstreamTemplateId, triggers };
};

/**
 * The minimal in-memory node shape profile resolution needs. Satisfied by
 * `DesignerNodeData`, `DesignerNode`, and creation-time node objects that are
 * not in state yet.
 */
export type ProfileNode = {
  id: number;
  typeId: string;
  config: unknown;
  childNodes?: Record<string, ProfileNode | ProfileNode[] | undefined>;
};

type ResolveChildProfile = (child: ProfileNode) => unknown;

const withProfile = <TChild extends ProfileNode>(child: TChild, resolveChildProfile: ResolveChildProfile): TChild => {
  const view = { ...child };
  // Lazy so a profile is only resolved when read; non-enumerable so spreads of the
  // view don't eagerly evaluate (or carry) it.
  Object.defineProperty(view, "profile", {
    get: () => resolveChildProfile(child),
    enumerable: false,
    configurable: true,
  });
  return view;
};

/**
 * A view of the node whose direct children expose their `profile` (lazily
 * resolved), for handing to manifest callbacks such as `getProfile`, `getTitle`
 * and `getChildContexts`.
 */
export const withProfileChildNodes = <TNode extends ProfileNode>(
  node: TNode,
  resolveChildProfile: ResolveChildProfile,
): TNode => ({
  ...node,
  childNodes: Object.fromEntries(
    Object.entries(node.childNodes ?? {}).map(([name, childOrChildren]) => [
      name,
      Array.isArray(childOrChildren)
        ? childOrChildren.map((child) => withProfile(child, resolveChildProfile))
        : childOrChildren
          ? withProfile(childOrChildren, resolveChildProfile)
          : undefined,
    ]),
  ) as TNode["childNodes"],
});

/**
 * Resolves profile for an in-memory node tree that is not in state yet (node
 * creation): recursion is by object rather than by id, uncached.
 */
export const resolveNodeProfileCreate = ({ getManifest }: { getManifest: GetManifestByTypeId }) => {
  const resolveNodeProfile = (node: ProfileNode): unknown => {
    const {
      designer: { getProfile, profileSchema },
    } = getManifest(node.typeId);
    if (!getProfile) {
      return undefined;
    }
    const profile = getProfile(withProfileChildNodes(node, resolveNodeProfile) as Parameters<typeof getProfile>[0]);
    return profileSchema ? profileSchema.parse(profile) : profile;
  };
  return resolveNodeProfile;
};

// Profile values cached by raw node data + monolingualize function: both change
// identity exactly when the profile's inputs change (a subtree edit replaces the
// data object; a language change replaces the memoized monolingualize), and
// both survive selector regeneration — so an unchanged node keeps a STABLE profile
// reference across state updates. Mirrors designerNodeCache in
// buildDesignerNode.ts, so profile identity is stable whenever built nodes are.
const profileCache = new WeakMap<DesignerNodeData, WeakMap<MonolingualizeConfig, unknown>>();

const profileCacheForNode = (node: DesignerNodeData): WeakMap<MonolingualizeConfig, unknown> => {
  const existing = profileCache.get(node);
  if (existing) return existing;
  const cachedByMonolingualize = new WeakMap<MonolingualizeConfig, unknown>();
  profileCache.set(node, cachedByMonolingualize);
  return cachedByMonolingualize;
};

/**
 * Resolves a node's public API ("profile") from its designer manifest's `getProfile`.
 * `undefined` for nodes without one. A node's `getProfile` sees its own children's
 * `profile` (resolved through this same function), so profile can compose up the tree
 * without any consumer reaching past direct children.
 *
 * `getProfile` receives the node with a monolingualized config (via
 * `getMonolingualDesignerNodeData`); the raw node data and the monolingualize
 * function are used only as profile cache keys.
 */
export const getNodeProfileByIdCreate = ({
  getManifest,
  getMonolingualDesignerNodeData,
  getDesignerNodeDataById,
  monolingualizeConfig,
}: {
  getManifest: GetManifestByTypeId;
  getMonolingualDesignerNodeData: GetMonolingualDesignerNodeData;
  getDesignerNodeDataById: GetDesignerNodeDataById;
  monolingualizeConfig: MonolingualizeConfig;
}) => {
  const getNodeProfileById: GetNodeProfileById = (id: number) => {
    const cachedByMonolingualize = profileCacheForNode(getDesignerNodeDataById(id));
    if (cachedByMonolingualize.has(monolingualizeConfig)) {
      getNodeProfileByIdProfiler.recordCacheHit();
      return cachedByMonolingualize.get(monolingualizeConfig);
    }

    const profilingEnabled = getNodeProfileByIdProfiler.isEnabled();
    const PROFILE_start = profilingEnabled ? performance.now() : 0;

    const node = getMonolingualDesignerNodeData(id);
    const {
      designer: { getProfile, profileSchema },
    } = getManifest(node.typeId);
    const profile = getProfile
      ? getProfile(withProfileChildNodes(node, (child) => getNodeProfileById(child.id)))
      : undefined;
    const validated = profile !== undefined && profileSchema ? profileSchema.parse(profile) : profile;

    if (profilingEnabled) {
      // Duration of this node's getProfile call, inclusive of any child profile resolved
      // (lazily, via withProfileChildNodes) during that call — those children also
      // record their own entry above, so nested time is double-counted here,
      // mirroring how buildDesignerNodeProfiling totals nested build time.
      getNodeProfileByIdProfiler.recordCacheMiss(performance.now() - PROFILE_start);
    }

    cachedByMonolingualize.set(monolingualizeConfig, validated);
    return validated;
  };

  return getNodeProfileById;
};

export type GetNodeProfileForRole = <TRole extends NodeDesignerRole>(
  id: number,
  role: TRole["role"],
) => TRole["profile"] | undefined;

/**
 * Role-guarded by-id profile access: `getNodeProfileForRole<SceneDesignerRole>(id, "scene")`.
 *
 * The guard is the node's effective runtime role STRING only — designer-roles
 * are types-only, so the profile shape is trusted from the implementor's typed
 * `getProfile` (its descriptor's `roles` forces the role's profile into its return
 * type). Returns `undefined` on role mismatch, and for implementors of the
 * runtime role that haven't adopted the designer-role contract (no `getProfile`).
 */
export const getNodeProfileForRoleCreate = ({
  getNodeProfileById,
  getNodeRoleById,
}: {
  getNodeProfileById: GetNodeProfileById;
  getNodeRoleById: (id: number) => string | undefined;
}): GetNodeProfileForRole => {
  const getNodeProfileForRole = <TRole extends NodeDesignerRole>(
    id: number,
    role: TRole["role"],
  ): TRole["profile"] | undefined =>
    getNodeRoleById(id) === role ? (getNodeProfileById(id) as TRole["profile"] | undefined) : undefined;
  return getNodeProfileForRole;
};
